import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_OPTIONS } from "../lib/permissions";
import { Logo } from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { PasswordInput } from "../components/PasswordInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { CheckCircle2, Clock, Info } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.pexels.com/photos/134878/pexels-photo-134878.jpeg";

function GoogleButton() {
  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <Button type="button" variant="outline" className="w-full" onClick={handleGoogle} data-testid="google-login-btn">
      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 4.75c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 1.46 14.97.5 12 .5A11 11 0 0 0 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.15 6.16-4.15z"/></svg>
      Continue with Google
    </Button>
  );
}

const AuthShell = ({ children }) => (
  <div className="grid min-h-screen lg:grid-cols-2">
    <div className="relative hidden lg:block">
      <img src={HERO} alt="Farm" className="absolute inset-0 h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1E2B22]/90 via-[#1E2B22]/30 to-transparent" />
      <div className="absolute bottom-0 left-0 p-12">
        <Logo size={60} className="mb-4 ring-2 ring-white/30" />
        <h2 className="font-display text-4xl font-extrabold tracking-tight text-white">
          Modav Farms Internal System
        </h2>
        <p className="mt-3 max-w-md text-white/70">
          Secure, employee-only management for livestock, crops, inventory, accounting and payroll — access granted by your administrator.
        </p>
      </div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-12">
      <div className="w-full max-w-sm fade-up">{children}</div>
    </div>
  </div>
);

export function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setNotice("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      const msg = apiErr(err);
      // Pending / rejected / deactivated accounts come back as 403 — show prominently.
      if (err?.response?.status === 403) {
        setNotice(msg);
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Welcome back</h1>
      <p className="mt-2 text-sm text-muted-foreground">Sign in to your farm dashboard.</p>
      {notice && (
        <div className="mt-6 flex items-start gap-2.5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="login-notice">
          <Clock className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{notice}</span>
        </div>
      )}
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            required data-testid="login-email" className="mt-1.5" placeholder="you@farm.com" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <Label htmlFor="password">Password</Label>
            <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">
              Forgot password?
            </Link>
          </div>
          <PasswordInput id="password" value={password} onChange={(e) => setPassword(e.target.value)}
            required data-testid="login-password" className="mt-1.5" placeholder="••••••••" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        New here?{" "}
        <Link to="/register" className="font-semibold text-primary" data-testid="go-register">Create an account</Link>
      </p>
    </AuthShell>
  );
}

export function Register() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", role_requested: "field_worker", password: "" });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [empId, setEmpId] = useState("");
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setEmpId(data.employee_id || "");
      setSubmitted(true);
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return (
      <AuthShell>
        <div className="text-center" data-testid="register-pending">
          <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
            <CheckCircle2 className="h-8 w-8 text-emerald-600" />
          </div>
          <h1 className="font-display text-2xl font-extrabold tracking-tight">Request submitted</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            Thanks, <b>{form.name}</b>. Your access request has been sent to the Modav Farms administrator.
            You'll be able to sign in once your account is <b>approved</b> — we'll email you the moment it is.
          </p>
          {empId && (
            <div className="mt-5 rounded-md border border-border bg-muted/50 px-4 py-3 text-sm" data-testid="register-empid">
              Your Employee ID: <b className="font-mono">{empId}</b>
            </div>
          )}
          <div className="mt-4 flex items-center justify-center gap-2 rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-700">
            <Clock className="h-4 w-4" /> Status: Pending approval
          </div>
          <Link to="/login"><Button variant="outline" className="mt-6 w-full" data-testid="back-to-login">Back to sign in</Button></Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-extrabold tracking-tight">Request employee access</h1>
      <p className="mt-2 text-sm text-muted-foreground">For Modav Farms staff only. An admin will approve your account.</p>
      <div className="mt-5 flex items-start gap-2.5 rounded-md border border-border bg-muted/40 px-4 py-3 text-xs text-muted-foreground" data-testid="register-empid-info">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <span>Your Employee ID is generated automatically once you submit — no need to enter one.</span>
      </div>
      <form onSubmit={submit} className="mt-6 space-y-3.5">
        <div>
          <Label htmlFor="name">Full name</Label>
          <Input id="name" value={form.name} onChange={upd("name")} required data-testid="register-name" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="phone">Phone</Label>
          <Input id="phone" value={form.phone} onChange={upd("phone")} required data-testid="register-phone" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="remail">Work email</Label>
          <Input id="remail" type="email" value={form.email} onChange={upd("email")} required data-testid="register-email" className="mt-1.5" />
        </div>
        <div>
          <Label htmlFor="role">Role requested</Label>
          <Select value={form.role_requested} onValueChange={(v) => setForm({ ...form, role_requested: v })}>
            <SelectTrigger className="mt-1.5" id="role" data-testid="register-role"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="rpass">Password</Label>
          <PasswordInput id="rpass" value={form.password} onChange={upd("password")} required data-testid="register-password" className="mt-1.5" placeholder="At least 6 characters" />
        </div>
        <Button type="submit" className="w-full" disabled={busy} data-testid="register-submit">
          {busy ? "Submitting…" : "Request access"}
        </Button>
      </form>
      <div className="my-5 flex items-center gap-3 text-xs text-muted-foreground">
        <div className="h-px flex-1 bg-border" /> OR <div className="h-px flex-1 bg-border" />
      </div>
      <GoogleButton />
      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already approved?{" "}
        <Link to="/login" className="font-semibold text-primary" data-testid="go-login">Sign in</Link>
      </p>
    </AuthShell>
  );
}
